import React, { useMemo, useState, useEffect } from 'react';
import { useConfirmation } from '../components/ui/ConfirmationDialog';
// FullCalendar CSS imports (plugin styles)
// CSS provided via CDN in index.html
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { InterviewService } from '../lib/services/interviews';
import CreateSlotDrawer from '../components/interviews/CreateSlotDrawer';
import BulkUploadSlotsModal from '../components/interviews/BulkUploadSlotsModal';

interface SlotEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  extendedProps?: Record<string, any>;
}

const Interviews: React.FC = () => {
  const { confirm } = useConfirmation();
  const [events, setEvents] = useState<SlotEvent[]>([]);
  const [timezone, setTimezone] = useState<string>(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [defaultDurationMin, setDefaultDurationMin] = useState<number>(30);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ startIso?: string; endIso?: string }>({});
  const [bulkOpen, setBulkOpen] = useState(false);

  const downloadSample = () => {
    // Defer implementation in modal to avoid bundle size; provide simple CSV fallback
    const headers = ['Title','Start','End','Mode','JoinLink','Capacity','Timezone','Location'];
    const sample = ['Interview slot','2025-10-09T10:00:00.000Z','2025-10-09T10:30:00.000Z','google_meet','https://meet.google.com/...','1','Asia/Kolkata','Bangalore HQ'];
    const csv = [headers.join(','), sample.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'interview_slots_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const slots = InterviewService.list();
    setEvents(slots.map(s => ({ id: s.id, title: s.title, start: s.startIso, end: s.endIso, extendedProps: s })));
  }, []);

  const calendarOptions = useMemo(() => ({
    plugins: [timeGridPlugin, interactionPlugin, dayGridPlugin],
    initialView: 'timeGridWeek',
    slotDuration: '00:30:00',
    selectable: true,
    selectMirror: true,
    nowIndicator: true,
    allDaySlot: false,
    timeZone: timezone,
    eventOverlap: true,
    select: (selectionInfo: any) => {
      const start = selectionInfo.start as Date;
      const end = selectionInfo.end as Date;
      const durationMs = end.getTime() - start.getTime();
      const finalEnd = durationMs > 0 ? end : new Date(start.getTime() + defaultDurationMin * 60000);
      setPrefill({ startIso: start.toISOString(), endIso: finalEnd.toISOString() });
      setDrawerOpen(true);
    },
    eventClick: (info: any) => {
      // Basic placeholder for future drawer/details
      confirm({
        title: 'Interview Slot Details',
        description: `Slot: ${info.event.title}\n${info.event.start?.toString()} - ${info.event.end?.toString()}`,
        variant: 'info',
        confirmText: 'OK',
        onConfirm: () => {}
      });
    },
    events,
  }), [events, timezone, defaultDurationMin]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        <label className="text-sm text-text-primary dark:text-text-primary">Timezone</label>
        <Input
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          placeholder="Timezone (e.g., Asia/Kolkata)"
        />
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-primary dark:text-text-primary">Duration</span>
          <Button variant="secondary" size="sm" onClick={() => setDefaultDurationMin(30)}>30m</Button>
          <Button variant="secondary" size="sm" onClick={() => setDefaultDurationMin(60)}>1h</Button>
          <Input type="number" className="w-24" value={defaultDurationMin} onChange={(e) => setDefaultDurationMin(Number(e.target.value || 30))} />
          <span className="text-sm text-text-tertiary dark:text-text-tertiary">minutes</span>
        </div>
        <div className="flex-1" />
        <Button onClick={() => { setPrefill({}); setDrawerOpen(true); }}>Create Slot</Button>
        <Button variant="outline" onClick={() => setBulkOpen(true)}>Bulk Upload</Button>
        <Button variant="secondary" onClick={downloadSample}>Download Sample</Button>
      </div>

      <div className="bg-white dark:bg-surface-dark-100 rounded-lg shadow-sm overflow-hidden border border-border-100 dark:border-border-dark-100">
        <FullCalendar {...calendarOptions} height="auto" />
      </div>

      <CreateSlotDrawer 
        isOpen={drawerOpen} 
        onClose={() => setDrawerOpen(false)} 
        defaultTimezone={timezone}
        initialStartIso={prefill.startIso}
        initialEndIso={prefill.endIso}
        onCreated={() => {
          const slots = InterviewService.list();
          setEvents(slots.map(s => ({ id: s.id, title: s.title, start: s.startIso, end: s.endIso, extendedProps: s })));
        }}
      />

      <BulkUploadSlotsModal
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onUploaded={() => {
          const slots = InterviewService.list();
          setEvents(slots.map(s => ({ id: s.id, title: s.title, start: s.startIso, end: s.endIso, extendedProps: s })));
        }}
      />
    </div>
  );
};

export default Interviews;



